<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="app-error-container">
    <div class="app-error-explanation">
      <template v-if="error === Error.Other">
        <FriedH1>Oops, something went wrong!</FriedH1>
        <FriedParagraph>
          The page couldn't load, we are sorry about this. Try to reload the page to get back to
          where you left off. You can always contact us.
        </FriedParagraph>
        <div class="buttons">
          <FriedButton
            :button-type="ButtonType.Secondary"
            data-external="app-error-back-to-login-button"
            @click="backToLogin"
          >
            Back to login page
          </FriedButton>
          <FriedButton data-external="app-error-contact-support-button" @click="reloadPage">
            Reload page
          </FriedButton>
        </div>
      </template>
      <template v-else>
        <template v-if="error === Error.NoSubscriptionFound">
          <FriedH1>Oops, we couldn't load your subscription</FriedH1>
          <FriedParagraph>
            An error occured when loading your subscription details. Try logging in again or contact
            support to get help.
          </FriedParagraph>
        </template>
        <template v-else-if="error === Error.InvalidModuleAccess">
          <FriedH1>Oops, something went wrong!</FriedH1>
          <FriedParagraph>
            Something is wrong with your module access. Please contact our support, they will be
            able to help you!
          </FriedParagraph>
        </template>
        <div class="buttons">
          <FriedButton
            :button-type="ButtonType.Secondary"
            data-external="app-error-contact-support-button"
            @click="contactSupport"
          >
            Contact support
          </FriedButton>
          <FriedButton data-external="app-error-back-to-login-button" @click="backToLogin">
            Back to login page
          </FriedButton>
        </div>
      </template>
    </div>
    <NotFoundSVG class="app-error-image" />
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent } from 'vue';
import { ButtonType } from '@getaccept/fried-tofu';
import { logOut } from '@getaccept/lib-shared/src/helpers/log-out.helper';
import { useEnvironment } from '@getaccept/lib-shared/src/composables/environment.composable';
import NotFoundSVG from './components/not-found/assets/NotFound.svg';
import { Error } from './stores/required-app-data.store';

export default defineComponent({
  components: {
    NotFoundSVG,
  },
  props: {
    error: {
      type: String as PropType<Error>,
      required: true,
    },
  },
  setup() {
    const { isLocalHost } = useEnvironment();

    const reloadPage = () => {
      window.location.reload();
    };

    const contactSupport = () => {
      window.open('http://help.getaccept.com/', '_blank');
    };

    const backToLogin = () => {
      logOut();

      if (isLocalHost.value) {
        window.location.href = '/login';
      }
    };

    return {
      ButtonType,
      Error,
      reloadPage,
      backToLogin,
      contactSupport,
    };
  },
});
</script>
<style lang="scss">
.app-error-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: var(--spacing-200);

  @media screen and (max-width: $md) {
    flex-direction: column-reverse;
    gap: var(--spacing-200);
  }

  .app-error-explanation {
    max-width: 600px;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-200);

    @media screen and (max-width: $md) {
      text-align: center;
    }

    .buttons {
      display: flex;
      flex-direction: row;
      gap: var(--spacing-100);
      margin-top: var(--spacing-100);

      @media screen and (max-width: $md) {
        justify-content: center;
      }
    }
  }

  .app-error-image {
    max-width: 600px;
  }
}
</style>
